import React, { useCallback, useEffect, useRef, useState } from "react";
import useToken from "./useToken";
import { useDispatch } from "react-redux";
import {
  setBidderTokens,
  setBidderUser,
  setBidderOnboarding,
  setOnboarding,
  clearBidderAuth,
  clearAuth,
  clearGuestUser,
} from "store";
import Router from "next/router";
import { getBidderOnboardingOfUser } from "services";
import { DateTime } from "luxon";
// import { createBidderOnboardingWithOutData } from "services/bidder";

const useAuth = function () {
  const dispatch = useDispatch();

  const { token } = useToken();
  const [authCheckComplete, setAuthCheckComplete] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(true);
  const isNavigatingRef = useRef(false);

  const safeReplace = useCallback((url: string) => {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    Router.replace(url);
  }, []);

  const logoutAndRedirect = useCallback(
    async (redirectTo: string = "/login") => {
      try {
        await fetch("/api/logout", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
      } catch (e) {}
      dispatch(clearBidderAuth());
      dispatch(clearAuth());
      dispatch(clearGuestUser());
      setAuthCheckComplete(true);
      safeReplace(redirectTo);
    },
    [dispatch, safeReplace]
  );
  const getISTDate = useCallback(() => {
    return DateTime.now().setZone("Asia/Kolkata").toFormat("yyyy-MM-dd");
  }, []);
  const is2FAVerifiedForToday = useCallback(
    (verified2faDate: any) => {
      return verified2faDate === getISTDate();
    },
    [getISTDate]
  );

  const check2faVerification = useCallback(async () => {
    const res = await fetch("/api/2fa", { method: "GET" });
    if (!res.ok) {
      console.error("Error fetching 2FA data:", res.status);
      return false;
    }
    const data = await res.json();
    const verified = is2FAVerifiedForToday(data.date);
    return verified;
  }, [is2FAVerifiedForToday]);

  useEffect(() => {
    (async () => {
      if (token && token.access_token) {
        if (token?.login_date) {
          const todayIST = DateTime.now().setZone("Asia/Kolkata");
          const loginDate = DateTime.fromFormat(
            token?.login_date,
            "yyyy-MM-dd",
            {
              zone: "Asia/Kolkata",
            }
          );
          const daysPassed = todayIST.diff(loginDate, "days").days;
          if (daysPassed > 30) {
            console.log("Session expired (1+ days). Logging out...");
            await logoutAndRedirect("/login?sessionExpired=true");
            return;
          }
          dispatch(setBidderTokens(token));
          dispatch(setBidderUser(token.data));
          const onboardingData: any = await getBidderOnboardingOfUser(
            token?.data?.uuid
          );
          dispatch(setOnboarding(onboardingData));
          dispatch(setBidderOnboarding(onboardingData));
          setAuthCheckComplete(true);
          const verified = await check2faVerification();
          if (!verified) {
            safeReplace("/verify");
            return;
          }
        } else {
          await logoutAndRedirect();
          dispatch(clearGuestUser());
          return;
        }
      }
      if (token && !token.isLoggedIn) {
        await logoutAndRedirect();
      }
    })();
  }, [token, logoutAndRedirect, check2faVerification, dispatch, safeReplace]);
  return {
    token,
    authCheckComplete,
    onboardingStatus,
  };
};

export default useAuth;
